// The puzzle as the owner of its board's constraints: registered when a
// puzzle opens, updated as the solver moves and as the board's head moves
// along the puzzle's line, released when the puzzle ends. The solver's own
// steps go through the owner's handle (the `review` given back here), so the
// board's enforcement lets them by. Pure of React: tested over a real
// `ConstraintStore` and review.
import { constraintDetail, constraintLabel, ownerId, type ConstraintHandle, type ConstraintRegistry, type OwnerId } from '@chess/api-constraints';
import { headPly, type Review } from '@chess/api-review';
import { puzzleConstraints } from './puzzle-constraints.ts';
import type { Solving } from './solving.ts';

const LABEL = constraintLabel('Puzzle');

export interface PuzzleOwner {
  /** The review as the solver uses it: every call runs as the constraints' owner while a puzzle holds them. */
  readonly review: Review;
  /** The solver's new state, with the card's prompt as the constraints' detail; none releases them. */
  constrain(solving: Solving | undefined, prompt: string | undefined): void;
}

/**
 * The owner of a puzzle's constraints on the review's board. Without a
 * registry nothing is constrained. `onAbandon` is called when the board's
 * constraints are released under the puzzle (the review opened afresh, its
 * game changed, its tab closed): the puzzle should end, so its card goes and
 * nothing it hid stays hidden.
 */
export function createPuzzleOwner(registry: ConstraintRegistry | undefined, review: Review, onAbandon: () => void, owner: OwnerId = ownerId('review-puzzles')): PuzzleOwner {
  let held: { handle: ConstraintHandle; unfollow: () => void } | undefined;
  let current: Solving | undefined;

  const constrain = (solving: Solving | undefined, prompt: string | undefined): void => {
    if (!solving) {
      current = undefined;
      held?.unfollow();
      held?.handle.release();
      held = undefined;
      return;
    }
    if (!registry) return;
    // Its board's scope was released under it (the game changed): start afresh.
    if (held && !held.handle.live()) {
      held.unfollow();
      held = undefined;
    }
    current = solving;
    const constraintsNow = () => puzzleConstraints(solving.status, solving.puzzle.ply, headPly(review.state()), solving.playOut?.playing);
    const detail = prompt ? constraintDetail(prompt) : undefined;
    if (!held) {
      const scope = review.state().scope;
      if (!scope) return;
      const handle = registry.register(scope, owner, LABEL, constraintsNow(), detail);
      // The range follows the board's head as the puzzle's line grows.
      const offState = review.subscribe((state) => {
        if (current) held?.handle.update(puzzleConstraints(current.status, current.puzzle.ply, headPly(state), current.playOut?.playing));
      });
      const offOwner = registry.subscribeOwner(scope, () => {
        if (held?.handle === handle && !handle.live()) onAbandon();
      });
      held = { handle, unfollow: () => (offState(), offOwner()) };
    } else {
      held.handle.update(constraintsNow(), detail);
    }
  };

  const asOwner = new Proxy(review, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') return value;
      return (...args: unknown[]) => (held ? held.handle.run(() => value.apply(target, args)) : value.apply(target, args));
    },
  });

  return { review: asOwner, constrain };
}
